import { QueryClientContext } from "@tanstack/react-query";
import { Suspense, useEffect, useRef } from "react";
import { useContext } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import type { Location } from "react-router-dom";

import {
  AccountDetailPage, AccountFormPage, AccountListPage, PlanYearDetailPage,
} from "@/features/accounts";
import { CarrierDetailPage, CarriersListPage } from "@/features/carriers";
import {
  CommitmentDetailModal, CommitmentDetailPage, CommitmentFormPage,
  CommitmentTrackerPage,
} from "@/features/commitments";
import {
  PlaybookEditorAliasRedirect, PlaybookEditorPage, PlaybookListPage,
} from "@/features/playbooks";
import { AcceptInvitationPage, SettingsPage } from "@/features/settings";
import {
  ContactDetailPage, OpportunitiesListPage, OpportunityDetailPage,
  ProspectDetailPage, SalesCrmPage, SalesImportPage,
} from "@/features/sales";
import { PermissionGuard, ProtectedRoute, useAuth } from "@/features/auth";
import { ApiError } from "@/api/client";
import { Dashboard } from "@/pages/Dashboard";
import { Login } from "@/pages/Login";
import { ResetPassword } from "@/pages/ResetPassword";
import { Signup } from "@/pages/Signup";
import { env } from "@/lib/env";
import { DashboardLayout } from "@/layouts/DashboardLayout";
import { AppShell, useReconMeQuery } from "@/features/recon/shell";
import { SHORTPAY_ROUTE_ELEMENTS } from "@/features/recon/routes";
import {
  SHORTPAY_ROUTES, isKnownAppHost, navigateToCanonicalHost, parseShellFromReconMe,
  shellFromHostname, shellHome,
  type AppShell as ShellKind,
} from "@/lib/navigation";

export interface CommitmentBackgroundLocationState {
  backgroundLocation?: Location;
}

type ReconQueryState = ReturnType<typeof useReconMeQuery>;

function isPublicRoute(pathname: string): boolean {
  return pathname === "/login"
    || pathname === "/signup"
    || pathname === "/reset-password"
    || pathname.startsWith("/invitations/");
}

function ReconStatus({
  status, message,
}: { status: "status" | "alert"; message: string }) {
  return (
    <div
      role={status}
      aria-live="polite"
      className="border-b border-border-subtle bg-raised px-4 py-2 text-center text-sm text-text-secondary"
    >
      {message}
    </div>
  );
}

function MarketingRedirect({ destination = "/dashboard" }: { destination?: string }) {
  useEffect(() => {
    const marketingUrl = new URL(env.marketingOrigin);
    if (window.location.origin === marketingUrl.origin) {
      window.location.replace(destination);
      return;
    }
    window.location.replace(marketingUrl.toString());
  }, [destination]);
  return null;
}

export default function App() {
  const location = useLocation();
  const state = location.state as CommitmentBackgroundLocationState | null;
  const backgroundLocation = state?.backgroundLocation;
  const auth = useAuth();
  const hostShell = shellFromHostname(window.location.hostname);
  const queryClient = useContext(QueryClientContext);

  // Recon bootstrap belongs to the authenticated app-host flow. Keeping it
  // below this boundary means existing BeneCRM and modal surfaces can still
  // render with their established provider assumptions, while production
  // continues to own the sole QueryClient/AuthProvider in main.tsx.
  if (
    queryClient
    && auth.status === "authenticated"
  ) {
    return (
      <ReconBootstrap
        location={location}
        backgroundLocation={backgroundLocation}
        hostShell={hostShell}
      />
    );
  }

  return (
    <AppContent
      location={location}
      backgroundLocation={backgroundLocation}
      hostShell={hostShell}
    />
  );
}

function ReconBootstrap({
  location, backgroundLocation, hostShell,
}: {
  location: Location;
  backgroundLocation?: Location;
  hostShell: ShellKind;
}) {
  const reconMe = useReconMeQuery(true);
  return (
    <AppContent
      location={location}
      backgroundLocation={backgroundLocation}
      hostShell={hostShell}
      reconMe={reconMe}
    />
  );
}

function AppContent({
  location, backgroundLocation, hostShell, reconMe,
}: {
  location: Location;
  backgroundLocation?: Location;
  hostShell: ShellKind;
  reconMe?: ReconQueryState;
}) {
  const canonicalNavigationStarted = useRef(false);
  const shellFromMe = parseShellFromReconMe(
    reconMe?.data?.shell, reconMe?.data?.canonical_app_domain,
  );
  const canonicalMismatch = Boolean(
    isKnownAppHost(window.location.hostname)
      && reconMe?.data?.canonical_app_domain
      && reconMe.data.canonical_app_domain !== window.location.hostname,
  );
  const reconUnresolved = reconMe
    ? reconMe.isLoading || reconMe.isError
    : false;
  const publicRoute = isPublicRoute(location.pathname);
  const routeShell = reconMe && !reconUnresolved ? shellFromMe : hostShell;

  useEffect(() => {
    if (
      !canonicalNavigationStarted.current
      && reconMe?.data?.canonical_app_domain
      && location.pathname
    ) {
      canonicalNavigationStarted.current = navigateToCanonicalHost(
        reconMe.data.canonical_app_domain,
        {
          host: window.location.hostname,
          protocol: window.location.protocol,
          pathname: window.location.pathname,
          search: window.location.search,
          hash: window.location.hash,
        },
      ) || canonicalNavigationStarted.current;
    }
  }, [reconMe?.data?.canonical_app_domain, location.pathname, location.search, location.hash]);

  if (canonicalMismatch) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas px-6 text-center text-sm text-text-secondary">
        Moving you to your workspace…
      </div>
    );
  }

  const reconStatus = reconMe?.error instanceof ApiError ? reconMe.error.status : null;
  const statusMessage = reconMe?.isLoading
    ? "Verifying workspace…"
    : reconStatus === 401
      ? "Workspace session is unavailable. Sign in again to continue."
      : "Temporary workspace lookup error. Retry this page.";
  if (reconUnresolved && !publicRoute && hostShell === "shortpay") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-canvas px-6">
        <ReconStatus
          status={reconMe?.isLoading ? "status" : reconStatus === 401 ? "alert" : "status"}
          message={statusMessage}
        />
      </div>
    );
  }

  const reconNotice = reconUnresolved && !publicRoute ? (
    <ReconStatus
      status={reconMe?.isLoading ? "status" : reconStatus === 401 ? "alert" : "status"}
      message={statusMessage}
    />
  ) : null;

  return (
    <>
      {reconNotice}
      {/* Suspense wraps the routed tree so the code-split Shortpay page chunks
          (SHORTPAY_ROUTE_ELEMENTS) can resolve without blocking the full app. */}
      <Suspense
        fallback={
          <div
            role="status"
            aria-label="Loading"
            className="flex min-h-[calc(100vh-56px)] items-center justify-center p-4 text-sm text-text-secondary"
          >
            <span className="sr-only">Loading</span>
            Loading…
          </div>
        }
      >
          <Routes location={backgroundLocation ?? location}>
            <Route path="/" element={<MarketingRedirect destination={shellHome(routeShell)} />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/invitations/:token" element={<AcceptInvitationPage />} />
            <Route element={<ProtectedRoute />}>
              <Route element={routeShell === "benecrm" ? <DashboardLayout /> : <Navigate to="/shortpay" replace />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/dashboard/sales-crm" element={<PermissionGuard permission="can_access_sales_crm"><SalesCrmPage /></PermissionGuard>} />
                <Route path="/dashboard/sales-crm/opportunities" element={<PermissionGuard permission="can_access_sales_crm"><OpportunitiesListPage /></PermissionGuard>} />
                <Route path="/dashboard/sales-crm/opportunities/:opportunityId" element={<PermissionGuard permission="can_access_sales_crm"><OpportunityDetailPage /></PermissionGuard>} />
                <Route path="/dashboard/sales-crm/prospects/:prospectId" element={<PermissionGuard permission="can_access_sales_crm"><ProspectDetailPage /></PermissionGuard>} />
                <Route path="/dashboard/sales-crm/import" element={<PermissionGuard permission="can_access_sales_crm"><SalesImportPage /></PermissionGuard>} />
                <Route path="/dashboard/sales-crm/contacts/:contactId" element={<PermissionGuard permission="can_access_sales_crm"><ContactDetailPage /></PermissionGuard>} />
                <Route path="/dashboard/accounts" element={<AccountListPage />} />
                <Route path="/dashboard/accounts/new" element={<AccountFormPage mode="create" />} />
                <Route path="/dashboard/accounts/:accountId/edit" element={<AccountFormPage mode="edit" />} />
                <Route path="/dashboard/accounts/:accountId/plan_years/:planYearId" element={<PlanYearDetailPage />} />
                <Route path="/dashboard/accounts/:accountId" element={<AccountDetailPage />} />
                <Route path="/dashboard/commitment-tracker" element={<CommitmentTrackerPage />} />
                <Route path="/dashboard/commitments/new" element={<CommitmentFormPage mode="create" />} />
                <Route path="/dashboard/commitments/:commitmentId/edit" element={<CommitmentFormPage mode="edit" />} />
                <Route path="/dashboard/commitments/:commitmentId" element={<CommitmentDetailPage />} />
                <Route path="/dashboard/carriers" element={<CarriersListPage />} />
                <Route path="/dashboard/carriers/:carrierId" element={<CarrierDetailPage />} />
                <Route path="/dashboard/playbooks" element={<PlaybookListPage />} />
                <Route path="/dashboard/playbooks/:playbookId/edit" element={<PlaybookEditorPage />} />
                <Route path="/dashboard/playbooks/:playbookId/editor" element={<PlaybookEditorAliasRedirect />} />
                <Route path="/dashboard/settings" element={<SettingsPage />} />
              </Route>
              <Route
                element={routeShell === "shortpay"
                  ? <AppShell shell="shortpay" tenantName={reconMe?.data?.slug ?? "Workspace"} tenantSlug={reconMe?.data?.slug} />
                  : <Navigate to="/dashboard" replace />}
              >
                {SHORTPAY_ROUTES.map((route) => (
                  <Route
                    key={route}
                    path={route}
                    element={SHORTPAY_ROUTE_ELEMENTS[route]}
                  />
                ))}
              </Route>
            </Route>
            <Route path="*" element={<Navigate to={shellHome(routeShell)} replace />} />
          </Routes>
        </Suspense>
      {backgroundLocation && (
        <Routes>
          <Route path="/dashboard/commitments/:commitmentId" element={<CommitmentDetailModal />} />
        </Routes>
      )}
    </>
  );
}
